import {HttpHeaders} from '@angular/common/http';
import {AfterViewInit, Component, OnDestroy} from '@angular/core';
import {OAuthService} from 'angular-oauth2-oidc';
import {AdminLoginService} from '../services/admin-login.service';
import {LogoutService} from '../services/logout.service';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {CloudUserProfile, TidUserProfile} from '../models/user-profile.model';
import {DMSConfigService} from '../services/dmsconfig.service';
import {UserService} from '../services/user.service';
import {CustomTranslateService} from '../services/custom-translate.service';

const DEFAULT_LANGUAGE = 'en';
const DMS = 'DMS';

@Component({
  selector: 'dms-aio-header',
  templateUrl: './aio-header.component.html'
})
export class AioHeaderComponent implements AfterViewInit, OnDestroy {
  private userProfileSubscription: Subscription;
  protected environmentValue:string;

  constructor(
    private readonly authService: OAuthService,
    private readonly oauthService: OAuthService,
    private readonly adminLoginService: AdminLoginService,
    private readonly logoutService: LogoutService,
    private readonly dmsConfigService: DMSConfigService,
    private readonly userService: UserService,
    private readonly translateService: CustomTranslateService
  ) {
  }

  ngAfterViewInit(): void {
    this.environmentValue = this.dmsConfigService.getConfig().APP_ENVIRONMENT;
    setTimeout(() => {
      this.initializeHeader();
    });

    this.userProfileSubscription = this.userService.tidUserProfile
      .pipe(filter(profile => !!profile))
      .subscribe((profile: TidUserProfile) => {
        this.setHeaderUser(profile);
      });
  }

  ngOnDestroy(): void {
    this.userProfileSubscription?.unsubscribe();
  }

  private async initializeHeader(): Promise<void> {
    const header = document.querySelector('ttl-header') as Element & { getUserProfileDetails?(): Promise<any> };
    if (!header) return;

    header.setAttribute('app-key', DMS);
    header.setAttribute('header-title', DMS);
    
    const accessToken = this.authService.getAccessToken();
    if (accessToken) {
      header.setAttribute('access-token', accessToken);
    }
    const tidUserProfile = this.userService.tidUserProfile.getValue();
    if (tidUserProfile) {
      this.setHeaderUser(tidUserProfile);
    } else if (header.getUserProfileDetails) {
      await this.fetchAndSetUserProfile(header);
    }
  }

  private async fetchAndSetUserProfile(header: Element & { getUserProfileDetails?(): Promise<any> }): Promise<void> {
    try {
      const details = await header.getUserProfileDetails();
      if (!details) return;

      const language = details.language || 'en';
      this.translateService.setLanguageTranslation(language);
      localStorage.setItem('userLanguage', language);

      const tidUser = this.createTidUserProfile(details);
      this.userService.tidUserProfile.next(tidUser);
    } catch (error) {
      console.error('Error getting user profile details from header:', error);
    }
  }

  private setHeaderUser(profile: TidUserProfile): void {
    const header = document.querySelector('ttl-header') as Element;
    if (!header) return;

    header.setAttribute('language', profile.language || DEFAULT_LANGUAGE);
    
  }

  private createTidUserProfile(cloudUserProfile: CloudUserProfile): TidUserProfile {
    const tidUserProfile = new TidUserProfile();
    tidUserProfile.tidEmail = cloudUserProfile.email;
    tidUserProfile.givenName = cloudUserProfile.firstName;
    tidUserProfile.familyName = cloudUserProfile.lastName;
    tidUserProfile.tidFullName = `${cloudUserProfile.lastName} ${cloudUserProfile.firstName}`;
    tidUserProfile.uuid = cloudUserProfile.uuid;
    tidUserProfile.language = cloudUserProfile.language;
    tidUserProfile.timeZone = cloudUserProfile.timezone;
    return tidUserProfile;
  }

  protected onHeaderEventFire(e: Event) {
    const customEvent = <CustomEvent>e;
    if (customEvent.detail.action === 'logOut') {
      this.handleLogout();
    } else if (customEvent.detail.action === 'customerSelectionChanged') {
      this.handleCustomerSelectionChange(customEvent.detail.value);
    }
  }

  private handleLogout() {
    let headers = new HttpHeaders();
    headers = headers.set(
      'Authorization',
      'Bearer ' + this.oauthService.getAccessToken()
    );
    this.clearCookiesAndLogout(headers);
  }

  private handleCustomerSelectionChange(customerId: string) {
    this.adminLoginService.setCustomerId(customerId);
    this.adminLoginService.setCustomerCookie$()
      .subscribe(() => {
        this.adminLoginService.emitCustomerSelectionChanged(true);
      });
  }

  private clearCookiesAndLogout(headers: HttpHeaders) {
    this.logoutService.clearCustomerCookie();
  }

}
